/**
 * Y GO Shuttle Services France - Page Prestations REFONTE
 * Objectif : Expliquer, structurer et rassurer (complément de la homepage)
 * Design : "Brushed Gold Relief" Premium
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, Plane, Ship, Building2, Users, Clock, MapPin, Phone, Calendar, 
  Check, ChevronRight, Loader2, Mail, Star, Shield, CreditCard,
  ArrowRight, CircleDollarSign, FileText, HelpCircle, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header, Footer } from '../components/layout';
import { useCMSPage } from '../hooks/useCMSPage';
import SafeImage from '../components/SafeImage';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=80';

export default function PrestationsPage() {
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('prestations');
  const [expandedService, setExpandedService] = useState(null);

  // Get hero data from CMS
  const banner = cmsContent?.banner || {};
  const heroImage = banner.image_url || DEFAULT_HERO_IMAGE;
  const heroTitle = banner.title || 'Nos Prestations VTC Premium';
  const heroSubtitle = banner.subtitle || 'Quatre expertises, un même niveau d\'excellence';

  // SEO
  useEffect(() => {
    const meta = cmsContent?.meta || {};
    document.title = meta.title || 'Nos Prestations VTC Premium | Y GO Shuttle Services';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = meta.description || 'Transferts aéroports, excursions Normandie, transport business et événements. Services VTC premium au Havre avec chauffeurs professionnels.';
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [cmsContent]);

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050509]">
      <Header activePage="Prestations" />
      
      <main>
        {/* HERO - Court et Structurant */}
        <section className="relative h-[450px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <SafeImage 
              src={heroImage}
              fallbackSrc={DEFAULT_HERO_IMAGE}
              alt="Nos prestations VTC"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-3xl space-y-4">
              <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
                VTC Premium • Le Havre & Normandie
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#E5E5E5] leading-tight" style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                {heroTitle.includes('Prestations') ? (
                  <>Nos <span className="text-[#C9A24D]">Prestations</span> VTC Premium</>
                ) : heroTitle}
              </h1>
              <p className="text-[#E5E5E5]/80 text-lg leading-relaxed max-w-2xl">
                {heroSubtitle}
              </p>
            </div>
          </div>
        </section>

        {/* LES 4 SERVICES EN PROFONDEUR */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl space-y-12">
            
            {/* Service 1: Transferts Aéroports & Gares */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <div className="w-16 h-16 bg-[#C9A24D]/12 flex items-center justify-center mb-4">
                    <Plane className="w-8 h-8 text-[#C9A24D]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#F5F5F5] mb-3">
                    Transferts <span className="text-[#C9A24D]">Aéroports & Gares</span>
                  </h2>
                  <p className="text-[#A7A7A7] text-sm mb-4">
                    Le service phare pour vos déplacements vers Paris et les grandes gares
                  </p>
                  <div className="text-[#C9A24D] font-bold text-xl mb-4">
                    Dès 180€
                  </div>
                  <Link to="/transferts">
                    <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none w-full">
                      Voir les tarifs détaillés
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  {/* À qui s'adresse ce service */}
                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      À qui s'adresse ce service ?
                    </h3>
                    <p className="text-[#D4D4D4] text-sm leading-relaxed">
                      Particuliers et professionnels ayant besoin d'un <strong>transport fiable et ponctuel</strong> vers les aéroports parisiens (CDG, Orly, Beauvais) ou les gares principales (Saint-Lazare, Rouen, Le Havre). Idéal pour <strong>départs matinaux</strong>, arrivées tardives ou <strong>correspondances serrées</strong>.
                    </p>
                  </div>

                  {/* Comment ça fonctionne */}
                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Comment ça fonctionne ?
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { step: '1', text: 'Réservation en ligne avec détails vol/train' },
                        { step: '2', text: 'Confirmation immédiate + suivi temps réel' },
                        { step: '3', text: 'Accueil avec pancarte nominative' },
                        { step: '4', text: 'Trajet direct, aide bagages incluse' }
                      ].map((item) => (
                        <div key={item.step} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-[#C9A24D] flex items-center justify-center flex-shrink-0 text-[#050509] text-xs font-bold">
                            {item.step}
                          </div>
                          <span className="text-[#D4D4D4] text-sm">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Ce qui est inclus */}
                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Ce qui est inclus
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        'Suivi vol/train en temps réel',
                        'Attente gratuite 60 min (aéroport) / 30 min (gare)',
                        'Accueil personnalisé avec pancarte',
                        'Aide aux bagages',
                        'Eau minérale + WiFi à bord',
                        'Paiement CB à bord ou par virement',
                        'Annulation gratuite jusqu\'à 24h',
                        'Facture détaillée (entreprises)'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                          <span className="text-[#D4D4D4] text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Exemple concret */}
                  <div className="bg-[#0B0B0B]/50 p-4 border-l-2 border-[#C9A24D]">
                    <p className="text-[#E5E5E5]/60 text-xs uppercase tracking-wider mb-2">Exemple concret</p>
                    <p className="text-[#D4D4D4] text-sm italic">
                      "Vous arrivez à CDG Terminal 2E à 6h du matin, votre navire part du Havre à 14h. Nous vous accueillons à la sortie avec votre nom, gérons vos bagages et vous conduisons directement au terminal croisières. Temps de trajet : 2h15. <strong className="text-[#C9A24D]">Vous arrivez à 8h30, sans stress.</strong>"
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 2: Croisiéristes & Excursions */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <div className="w-16 h-16 bg-[#C9A24D]/12 flex items-center justify-center mb-4">
                    <Ship className="w-8 h-8 text-[#C9A24D]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#F5F5F5] mb-3">
                    Croisiéristes & <span className="text-[#C9A24D]">Excursions</span>
                  </h2>
                  <p className="text-[#A7A7A7] text-sm mb-4">
                    Service dédié aux passagers de croisières avec retour garanti à l'heure
                  </p>
                  <div className="text-[#C9A24D] font-bold text-xl mb-4">
                    Dès 160€
                  </div>
                  <Link to="/croisieres-excursions">
                    <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none w-full">
                      Découvrir les excursions
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      À qui s'adresse ce service ?
                    </h3>
                    <p className="text-[#D4D4D4] text-sm leading-relaxed">
                      Passagers de croisières en escale au Havre souhaitant découvrir la Normandie de manière <strong>autonome et flexible</strong>, sans dépendre des excursions de groupe. Familles, couples ou petits groupes cherchant une <strong>expérience personnalisée</strong> avec retour garanti avant l'heure d'embarquement.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Comment ça fonctionne ?
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { step: '1', text: 'Indiquez votre navire et horaire d\'embarquement' },
                        { step: '2', text: 'Choisissez votre circuit ou créez-le sur mesure' },
                        { step: '3', text: 'Accueil au terminal avec panneau nominatif' },
                        { step: '4', text: 'Retour garanti 1h avant embarquement' }
                      ].map((item) => (
                        <div key={item.step} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-[#C9A24D] flex items-center justify-center flex-shrink-0 text-[#050509] text-xs font-bold">
                            {item.step}
                          </div>
                          <span className="text-[#D4D4D4] text-sm">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Excursions populaires
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {[
                        { dest: 'Étretat', time: '3-4h', price: '180€' },
                        { dest: 'Honfleur', time: '3-4h', price: '180€' },
                        { dest: 'D-Day Beaches', time: '8-10h', price: '450€' }
                      ].map((exc) => (
                        <div key={exc.dest} className="bg-[#0B0B0B]/50 p-3 text-center">
                          <p className="text-[#F5F5F5] font-semibold text-sm mb-1">{exc.dest}</p>
                          <p className="text-[#A7A7A7] text-xs mb-1">{exc.time}</p>
                          <p className="text-[#C9A24D] text-sm font-semibold">Dès {exc.price}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#0B0B0B]/50 p-4 border-l-2 border-[#C9A24D]">
                    <p className="text-[#E5E5E5]/60 text-xs uppercase tracking-wider mb-2">Exemple concret</p>
                    <p className="text-[#D4D4D4] text-sm italic">
                      "Votre navire accoste à 8h, embarquement à 17h. Nous vous proposons Honfleur + Deauville (6h) avec retour à 16h au terminal. <strong className="text-[#C9A24D]">Tranquillité garantie.</strong>"
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 3: Transport Business */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <div className="w-16 h-16 bg-[#C9A24D]/12 flex items-center justify-center mb-4">
                    <Building2 className="w-8 h-8 text-[#C9A24D]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#F5F5F5] mb-3">
                    Transport <span className="text-[#C9A24D]">Business</span>
                  </h2>
                  <p className="text-[#A7A7A7] text-sm mb-4">
                    Solutions professionnelles pour entreprises exigeantes
                  </p>
                  <div className="text-[#C9A24D] font-bold text-xl mb-4">
                    Sur devis
                  </div>
                  <Link to="/entreprises">
                    <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none w-full">
                      Solutions entreprises
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      À qui s'adresse ce service ?
                    </h3>
                    <p className="text-[#D4D4D4] text-sm leading-relaxed">
                      Entreprises, cadres et professionnels nécessitant un <strong>transport discret, ponctuel et représentatif</strong> pour rendez-vous clients, déplacements inter-sites, accueil de délégations ou séminaires. Adapté aux <strong>comptes entreprises</strong> avec facturation mensuelle.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Formules disponibles
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {[
                        { title: 'À la course', desc: 'Réservation ponctuelle', icon: Car },
                        { title: 'Mise à disposition', desc: 'Demi-journée ou journée', icon: Clock },
                        { title: 'Compte mensuel', desc: 'Facturation groupée', icon: FileText }
                      ].map((formula) => (
                        <div key={formula.title} className="bg-[#0B0B0B]/50 p-4 text-center">
                          <formula.icon className="w-6 h-6 text-[#C9A24D] mx-auto mb-2" />
                          <p className="text-[#F5F5F5] font-semibold text-sm mb-1">{formula.title}</p>
                          <p className="text-[#A7A7A7] text-xs">{formula.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Services inclus
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        'Chauffeur en costume',
                        'Véhicule premium récent',
                        'Discrétion et confidentialité absolue',
                        'Réservation prioritaire',
                        'Facturation mensuelle',
                        'Reporting détaillé des courses'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                          <span className="text-[#D4D4D4] text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#0B0B0B]/50 p-4 border-l-2 border-[#C9A24D]">
                    <p className="text-[#E5E5E5]/60 text-xs uppercase tracking-wider mb-2">Exemple concret</p>
                    <p className="text-[#D4D4D4] text-sm italic">
                      "Votre PDG reçoit un client important à Rouen à 14h, puis dîner d'affaires à Honfleur à 20h. Un seul chauffeur, deux destinations, <strong className="text-[#C9A24D]">discrétion garantie.</strong> Facturation mensuelle groupée."
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 4: Événements & Cérémonies */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <div className="w-16 h-16 bg-[#C9A24D]/12 flex items-center justify-center mb-4">
                    <Star className="w-8 h-8 text-[#C9A24D]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#F5F5F5] mb-3">
                    Événements & <span className="text-[#C9A24D]">Cérémonies</span>
                  </h2>
                  <p className="text-[#A7A7A7] text-sm mb-4">
                    Transport élégant pour vos moments importants
                  </p>
                  <div className="text-[#C9A24D] font-bold text-xl mb-4">
                    Sur devis
                  </div>
                  <Link to="/contact">
                    <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none w-full">
                      Demander un devis
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      À qui s'adresse ce service ?
                    </h3>
                    <p className="text-[#D4D4D4] text-sm leading-relaxed">
                      Particuliers organisant un <strong>mariage, anniversaire, gala ou événement familial</strong> nécessitant un transport <strong>élégant et coordonné</strong>. Entreprises organisant des soirées, réceptions ou cérémonies VIP avec besoin de <strong>flotte coordonnée</strong>.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Cas d'usage typiques
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        'Mariages (mariés + invités)',
                        'Anniversaires & célébrations',
                        'Soirées d\'entreprise & galas',
                        'Transport invités VIP',
                        'Cérémonies officielles',
                        'Réceptions châteaux & domaines'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Star className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                          <span className="text-[#D4D4D4] text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[#C9A24D] font-semibold text-sm uppercase tracking-wider mb-3">
                      Options personnalisables
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        'Véhicules décorables (rubans, fleurs)',
                        'Champagne à bord (sur demande)',
                        'Coordination avec traiteur/lieu',
                        'Flotte multiple coordonnée',
                        'Chauffeur en costume ou tenue spéciale',
                        'Planning multi-trajets'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                          <span className="text-[#D4D4D4] text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#0B0B0B]/50 p-4 border-l-2 border-[#C9A24D]">
                    <p className="text-[#E5E5E5]/60 text-xs uppercase tracking-wider mb-2">Exemple concret</p>
                    <p className="text-[#D4D4D4] text-sm italic">
                      "Mariage au château de X : transport des mariés de l'hôtel à la cérémonie (10h), puis vers la réception (15h). Navette invités gare du Havre ↔ château (20 personnes, 3 véhicules). <strong className="text-[#C9A24D]">Coordination parfaite.</strong>"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESSUS DE RÉSERVATION */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Comment <span className="text-[#C9A24D]">réserver</span> ?
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Un processus simple et transparent, en ligne ou par téléphone
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { 
                  num: '1',
                  title: 'Choisissez',
                  desc: 'Sélectionnez votre service et renseignez les détails (dates, lieux, nombre de passagers)',
                  icon: Calendar
                },
                { 
                  num: '2',
                  title: 'Recevez',
                  desc: 'Confirmation immédiate par email avec détails de la course et contact chauffeur',
                  icon: Mail
                },
                { 
                  num: '3',
                  title: 'Suivez',
                  desc: 'Suivi en temps réel de votre véhicule et possibilité de contact direct',
                  icon: MapPin
                },
                { 
                  num: '4',
                  title: 'Profitez',
                  desc: 'Voyage en toute sérénité avec notre service premium de bout en bout',
                  icon: CheckCircle2
                }
              ].map((step) => (
                <div key={step.num} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                  <div className="w-12 h-12 bg-[#C9A24D] flex items-center justify-center mx-auto mb-4 text-[#050509] font-bold text-xl">
                    {step.num}
                  </div>
                  <step.icon className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-2">
                    {step.title}
                  </h3>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/booking">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-10 py-4 rounded-none text-base">
                  <Calendar className="w-5 h-5 mr-2" />
                  Réserver en ligne maintenant
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* GARANTIES & ENGAGEMENTS APPROFONDIS */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Nos <span className="text-[#C9A24D]">Garanties</span> & Engagements
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Au-delà du transport, un engagement de service à chaque trajet
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: Clock,
                  title: 'Ponctualité Garantie',
                  points: [
                    'Suivi GPS en temps réel de votre vol ou train',
                    'Chauffeur toujours à l\'heure, même en cas de retard',
                    'Notification SMS 30 min avant arrivée',
                    'Garantie retour à l\'heure pour croisiéristes'
                  ]
                },
                {
                  icon: Shield,
                  title: 'Sécurité & Assurances',
                  points: [
                    'Chauffeurs professionnels agréés VTC',
                    'Véhicules assurés tous risques',
                    'Contrôle technique rigoureux',
                    'Assistance 24/7 en cas d\'imprévu'
                  ]
                },
                {
                  icon: CircleDollarSign,
                  title: 'Transparence Tarifaire',
                  points: [
                    'Prix fixé à la réservation, jamais de surprise',
                    'Attente gratuite : 60 min aéroport, 30 min gare',
                    'Annulation gratuite jusqu\'à 24h avant',
                    'Aucun frais caché (péages, parkings inclus)'
                  ]
                },
                {
                  icon: Star,
                  title: 'Excellence de Service',
                  points: [
                    'Véhicules premium climatisés (Mercedes, Tesla)',
                    'WiFi gratuit + eau minérale à bord',
                    'Chauffeurs bilingues français/anglais',
                    'Aide aux bagages systématique'
                  ]
                }
              ].map((guarantee) => (
                <div key={guarantee.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                      <guarantee.icon className="w-6 h-6 text-[#C9A24D]" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                      {guarantee.title}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {guarantee.points.map((point, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TARIFICATION DÉTAILLÉE */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                <span className="text-[#C9A24D]">Tarification</span> Transparente
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Prix forfaitaires basés sur la distance, sans surprise
              </p>
            </div>

            {/* Grille tarifaire exemples */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm mb-8">
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-6">
                Exemples de trajets depuis Le Havre
              </h3>
              <div className="space-y-3">
                {[
                  { from: 'Le Havre', to: 'Honfleur', time: '30 min', price: '80-100€', vehicle: 'Berline' },
                  { from: 'Le Havre', to: 'Deauville', time: '45 min', price: '120-140€', vehicle: 'Berline' },
                  { from: 'Le Havre', to: 'CDG Aéroport', time: '2h15', price: '280-320€', vehicle: 'Berline' },
                  { from: 'Le Havre', to: 'Paris Centre', time: '2h', price: '260-300€', vehicle: 'Berline' },
                  { from: 'Le Havre', to: 'Excursion Étretat (4h)', time: '4h A/R', price: '180-220€', vehicle: 'Selon groupe' },
                  { from: 'Le Havre', to: 'Excursion D-Day (10h)', time: '10h A/R', price: '450-550€', vehicle: 'Selon groupe' }
                ].map((route, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B0B0B]/50 p-4 border-l-2 border-[#C9A24D]/30">
                    <div className="flex items-center gap-4 flex-1">
                      <MapPin className="w-4 h-4 text-[#C9A24D] flex-shrink-0" />
                      <span className="text-[#F5F5F5] text-sm font-medium">{route.from} → {route.to}</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-[#A7A7A7]">{route.time}</span>
                      <span className="text-[#A7A7A7]">{route.vehicle}</span>
                      <span className="text-[#C9A24D] font-semibold min-w-[100px] text-right">{route.price}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[#A7A7A7] text-xs mt-4 text-center">
                * Prix indicatifs berline 1-3 passagers. Tarifs van/minibus disponibles sur demande.
              </p>
            </div>

            {/* Modes de paiement */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                <CreditCard className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                <h4 className="text-[#F5F5F5] font-semibold mb-2">Paiement CB</h4>
                <p className="text-[#A7A7A7] text-sm">À bord ou en ligne sécurisé</p>
              </div>
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                <FileText className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                <h4 className="text-[#F5F5F5] font-semibold mb-2">Virement</h4>
                <p className="text-[#A7A7A7] text-sm">Facturation entreprise acceptée</p>
              </div>
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                <Users className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                <h4 className="text-[#F5F5F5] font-semibold mb-2">Compte entreprise</h4>
                <p className="text-[#A7A7A7] text-sm">Facturation mensuelle groupée</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Questions <span className="text-[#C9A24D]">Fréquentes</span>
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: 'Que se passe-t-il si mon vol est retardé ?',
                  a: 'Nous suivons votre vol en temps réel et ajustons automatiquement l\'heure de prise en charge. Aucun frais supplémentaire pour un retard de vol. Votre chauffeur vous attendra.'
                },
                {
                  q: 'Puis-je annuler ou modifier ma réservation ?',
                  a: 'Oui, l\'annulation est gratuite jusqu\'à 24h avant le départ. Les modifications sont possibles jusqu\'à 12h avant (sous réserve de disponibilité). Contactez-nous par téléphone ou email.'
                },
                {
                  q: 'Acceptez-vous les paiements par carte bancaire ?',
                  a: 'Oui, nous acceptons les paiements CB à bord (sans frais), paiement en ligne sécurisé lors de la réservation, ou virement bancaire pour les entreprises. Une facture détaillée vous est envoyée par email.'
                },
                {
                  q: 'Combien de bagages puis-je transporter ?',
                  a: 'Berline : 3 valises cabine + 3 bagages à main. Van : 7 valises + bagages à main. Pour des volumes exceptionnels, précisez-le lors de la réservation pour que nous adaptions le véhicule.'
                },
                {
                  q: 'Vos chauffeurs parlent-ils anglais ?',
                  a: 'Oui, tous nos chauffeurs parlent français et anglais couramment. Certains parlent également allemand, espagnol ou italien. Précisez votre langue lors de la réservation si besoin.'
                },
                {
                  q: 'Proposez-vous des sièges enfants ?',
                  a: 'Oui, nous fournissons gratuitement des sièges auto pour enfants sur simple demande lors de la réservation. Précisez l\'âge et le poids de l\'enfant pour que nous préparions le siège adapté (groupe 0+, 1, 2 ou 3).'
                }
              ].map((faq, i) => (
                <div 
                  key={i} 
                  className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-5 h-5 text-[#C9A24D]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#F5F5F5] font-semibold mb-2">{faq.q}</h3>
                      <p className="text-[#A7A7A7] text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <p className="text-[#A7A7A7] text-sm mb-4">
                Vous avez une autre question ? Notre équipe est à votre écoute.
              </p>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-8 py-3 rounded-none"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-20 bg-gradient-to-r from-[#111111] to-[#1F1F1F] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
              Prêt à réserver votre <span className="text-[#C9A24D]">transport</span> ?
            </h2>
            <p className="text-[#A7A7A7] text-base mb-8 max-w-2xl mx-auto">
              Réservation en ligne en 3 minutes ou appelez-nous pour un service personnalisé
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/booking">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-10 py-4 rounded-none text-base">
                  <Calendar className="w-5 h-5 mr-2" />
                  Réserver en ligne
                </Button>
              </Link>
              <a href="tel:+33278780404">
                <Button
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-10 py-4 rounded-none text-base"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  +33 2 78 78 04 04
                </Button>
              </a>
            </div>

            {/* Contact rapide */}
            <div className="grid sm:grid-cols-2 gap-6 mt-12 max-w-2xl mx-auto">
              <a 
                href="tel:+33278780404"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Phone className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  Assistance 24/7
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Réponse immédiate</p>
              </a>
              <a 
                href="mailto:booking@y-go.fr"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Mail className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  Email booking
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Réponse sous 2h</p>
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
